<template>
  <div>
    <!-- 기본정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :board="this.board"
      :is-print="isPrint"
      :options="[
        { type: 'date', label: '점검일' },
        { type: 'text', label: '점검장소' },
        { type: 'disaster', label: '재해형태' },
      ]"
    />

    <!--    점검대상-->
    <vs-row class="mt-6">
      <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
        <h5><i class="dot" /> 점검대상</h5>
      </vs-col>
    </vs-row>
    <div class="grid grid-cols-12 gap-x-6 gap-y-4 mt-4">
      <div :class="isPrint ? 'col-span-2' : 'col-span-12 xl:col-span-2'" class="flex items-center" v-for="checkbox in form.inspectList" v-bind:key="checkbox.id">
        <div v-if="isPrint || board.readonly" class="flex">
          <div v-if="checkbox.check" style="background: #6670cb" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
            <vs-icon icon="check" size="x-small" style="background-color: #6670cb"></vs-icon>
          </div>
          <div v-else style="border: 1px solid #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
            <vs-icon icon="check" size="x-small"></vs-icon>
          </div>
          {{ checkbox.label }}
        </div>
        <label v-else class="flex">
          <vs-checkbox color="secondary" :readonly="board.readonly" v-model="checkbox.check" @change="checkForm"> </vs-checkbox>
          {{ checkbox.label }}
        </label>
      </div>
    </div>

    <!-- 체감온도 -->
    <div v-if="form.inspectList[9] && form.inspectList[9].check">
      <vs-row class="mt-6">
        <vs-col vs-type="flex" vs-justify="left" vs-align="center" w="12">
          <h5><i class="dot" /> 체감온도</h5>
          <vx-tooltip title="체감온도 산출표" :html="'자세한 정보를 보려면 클릭하세요'" style="margin-left: 4px; display: flex; align-items: center; justify-content: center" position="top" delay=".1s">
            <div style="color: #7e72f2; cursor: pointer" class="flex flex-row items-center ml-4" @click="showPopup = true">
              <feather-icon icon="AlertCircleIcon" style="width: 18px" />
              <span class="ml-2" style="font-size: 14px">체감온도 산출표</span>
            </div>
          </vx-tooltip>
        </vs-col>
      </vs-row>

      <div class="w-full mt-4" style="border: 1px solid #cdcdcd; padding: 21px; font-size: 14px">
        <div class="flex flex-row items-center gap-4">
          <div>온도(℃)</div>
          <div><number-input v-model="form.temperature" :max="40" :min="-20" /></div>
          <div class="ml-3">습도(%)</div>
          <div><number-input v-model="form.humidity" :max="100" :min="0" /></div>
        </div>
        <div class="flex flex-row items-center gap-4 mt-4">
          <div>체감온도 :</div>
          <div style="font-size: 18px; font-weight: 700; color: #3c3c3c">{{ form.temperature_feel }}℃</div>
          <div v-if="form.temperature_feel >= 38" class="ml-3 temp-tag" style="background: #ea5455">위험</div>
          <div v-else-if="form.temperature_feel >= 35" class="ml-3 temp-tag" style="background: #ff9f43">경고</div>
          <div v-else-if="form.temperature_feel >= 33" class="ml-3 temp-tag" style="background: #ffcd43">주의</div>
          <div v-else-if="form.temperature_feel >= 31" class="ml-3 temp-tag" style="background: #00cfe8">관심</div>
        </div>
      </div>
    </div>

    <!--    표-->
    <TableInspectMerge
      v-for="inspection in form.inspectList"
      :key="`inspection_${inspection.title}`"
      v-show="inspection.check"
      class="mt-5"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="inspection.title"
      :tableHeaderTitle="inspection.tableHeaderTitle ? inspection.tableHeaderTitle : '점검항목'"
      v-model="inspection.list"
      :table-header-note="[{ text: '비고(보완계획)', width: '389' }]"
      :table-header-check="[
        { text: '양호', value: 'Yes', width: '82' },
        { text: '미흡', value: 'No', width: '82' },
        { text: '해당없음', value: 'N/A', width: '82' },
      ]"
      :only-pc="isPrint"
      contentBefore="•"
    >
      <template #customHead>
        <tr>
          <th rowspan="2" colspan="2">점검항목</th>
          <th colspan="3">점검결과</th>
          <th rowspan="2">비고(보완계획)</th>
        </tr>
        <tr>
          <th>양호</th>
          <th>미흡</th>
          <th>해당없음</th>
        </tr>
      </template>
    </TableInspectMerge>

    <!--    기타점검내용-->
    <table class="mt-5">
      <tr>
        <th>점검내용</th>
      </tr>
      <tr>
        <td>
          <span v-if="isPrint" class="print-textarea"> {{ form.기타점검내용 }} </span>
          <ml-textarea v-else class="w-full" rows="5" v-model="form.기타점검내용" :readonly="board.readonly" />
        </td>
      </tr>
    </table>

    <!-- 현장근로자 의견 청취 및 특이사항 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i>현장근로자 의견 청취 및 특이사항</h5>
      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <div style="line-height: 1.4; word-break: keep-all">현장근로자 의견 청취</div>
          <p v-if="isPrint" class="print-input">
            {{ form.inputC_1.fileName }}
          </p>
          <file-uploader v-else :readonly="board.readonly" v-model="form.inputC_1" />
        </div>
        <div class="writeGridLineItem">
          <div>특이사항</div>
          <span v-if="isPrint" class="print-textarea"> {{ form.inputC_2 }} </span>
          <ml-textarea v-else rows="5" v-model="form.inputC_2" :readonly="board.readonly" />
        </div>
      </div>
    </div>

    <!-- 유해·위험요인 개선 결과 -->
    <ImprovementTable :readonly="board.readonly" :isPrint="isPrint" v-model="form.개선결과" />

    <!-- 파일첨부 -->
    <file-upload-module id="fileList" class="w-full mt-4" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>

    <!-- 체감온도 산출표 팝업 컴포넌트 -->
    <TemperatureFeelTablePopup :visible.sync="showPopup" />
  </div>
</template>

<script>
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import FileUploader from "@/components/FileUploader";
import TableInspectEp from "@/components/TableInspectCustomEp";
import TableInspectMerge from "@/components/TableInspectMerge.vue";
import ImprovementTable from "./improvementTable.vue";
import NumberInput from "@/components/NumberInput.vue";
import TemperatureFeelTablePopup from "@/views/boards/single-page/198/TemperatureFeelTablePopup.vue";

import inspectData from "./inspectData";

export default {
  components: {
    TableInspectMerge,
    TableInspectEp,
    FileUploader,
    PhotoUploadGridModule,
    FieldSelectModule,
    FileUploadModule,
    ImprovementTable,
    NumberInput,
    TemperatureFeelTablePopup
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  data() {
    return {
      showPopup: false,
      form: {
        stdInfo: {},
        inputC_1: {},
        inputC_2: "",

        typeTitleList: [],
        inspectList: [],
        photoList: [],
        fileList: [],

        temperature: 0,
        humidity: 0,
        temperature_feel: 0,

        개선결과: [],

        기타점검내용: "",
      },

      inspectTable: JSON.parse(JSON.stringify(inspectData)),
    };
  },
  computed: {
    postId() {
      return this.$route.params.postId;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (!value) return;

        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (this.postId && key === "개선결과" && value[key]) {
            value[key].forEach((item) => {
              const 원본문자 = item.개선내용;

              // 담당자와 개선일자 있는지 검사
              const has담당자 = 원본문자.includes("- 담당자");
              const has개선일자 = 원본문자.includes("- 개선일자");

              if (has담당자 && has개선일자) {
                console.log("원본", 원본문자);
                // 값 추출
                const 담당자Match = 원본문자.match(/- 담당자\s*:(.*)/);
                const 개선일자Match = 원본문자.match(/- 개선일자\s*:(.*)/);
                const 개선내용Match = 원본문자.match(/- 개선내용\s*:?\s*([\s\S]*?)(?:- 개선일자|$)/);

                const 담당자 = 담당자Match ? 담당자Match[1].trim() : null;
                const 개선일자 = 개선일자Match ? 개선일자Match[1].trim() : null;
                const 개선내용 = 개선내용Match ? 개선내용Match[1].trim() : "";

                item.개선내용담당자 = 담당자;
                item.개선내용개선일자 = 개선일자;
                item.개선내용 = 개선내용;
              }
            });
          }

          if (value[key]) {
            // inspectList는 별도 처리
            if (key === 'inspectList') {
              // 레거시 데이터 마이그레이션 처리
              if (!value[key] || value[key].length === 0) {
                this.form[key] = this.inspectTable;
              } else if (value[key].length < this.inspectTable.length) {
                // 레거시 데이터 (9개 항목 -> 11개 항목)
                this.form[key] = this.migrateInspectList(value[key]);
              } else {
                this.form[key] = value[key];
              }
            } else {
              this.form[key] = value[key];
            }
          }
        }

        // inspectList가 없는 경우 초기화
        if (!this.form.inspectList || this.form.inspectList.length === 0) {
          this.form.inspectList = this.inspectTable;
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        value.title = this.board.writer.department;
        this.$emit("input", value);
      },
    },
    // 온도나 습도가 변경될 때마다 체감온도를 실시간으로 계산
    "form.temperature"() {
      this.calculateTemperatureFeel();
    },
    "form.humidity"() {
      this.calculateTemperatureFeel();
    },
  },
  mounted() {
    // 컴포넌트 마운트 시 체감온도 계산
    this.calculateTemperatureFeel();
  },
  methods: {
    /**
     * 레거시 데이터를 현재 폼 구조로 마이그레이션
     * @param {Array} legacyList 레거시 inspectList 데이터
     * @returns {Array} 마이그레이션된 inspectList
     */
    migrateInspectList(legacyList) {
      // 기본 템플릿 복사
      const template = JSON.parse(JSON.stringify(this.inspectTable));

      // 레거시 데이터를 ID 기준으로 매핑
      const result = template.map(templateItem => {
        const legacyItem = legacyList.find(item => item.id === templateItem.id);

        if (legacyItem) {
          // 레거시 항목이 있으면 병합 (check 속성 보장)
          return {
            ...templateItem,
            ...legacyItem,
            check: legacyItem.check !== undefined ? legacyItem.check : false,
            list: this.migrateInspectItemList(templateItem.list, legacyItem.list)
          };
        }

        // 레거시에 없는 신규 항목은 템플릿 기본값 사용
        return templateItem;
      });

      return result;
    },

    /**
     * inspectList 내부의 list 항목 마이그레이션
     * @param {Array} templateList 템플릿 list
     * @param {Array} legacyList 레거시 list
     * @returns {Array} 마이그레이션된 list
     */
    migrateInspectItemList(templateList, legacyList) {
      if (!legacyList) return templateList;

      return templateList.map(templateItem => {
        const legacyItem = legacyList.find(item => item.id === templateItem.id);
        return legacyItem ? { ...templateItem, ...legacyItem } : templateItem;
      });
    },

    checkForm() {
      this.form.typeTitleList = [];
      for (const data of this.form.inspectList) {
        if (data.check) {
          this.form.typeTitleList.push(data.label);
        }
      }
    },
    updateListCheck(indexList, indexTableRows, text) {
      this.form.inspectList[indexList].list[indexTableRows].is_checked = text;
    },

    /**
     * 습구온도(Tw) 계산
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 습구온도
     */
    getTw(ta, rh) {
      // 습구온도 계산공식 (Java 코드와 동일)
      return ta * Math.atan(0.151977 * Math.pow(rh + 8.313659, 0.5)) + Math.atan(ta + rh) - Math.atan(rh - 1.67633) + 0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 4.686035;
    },

    /**
     * 여름철 체감온도 계산 (5월 ~ 9월)
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 체감온도
     */
    getInSummer(ta, rh) {
      const tw = this.getTw(ta, rh);
      return -0.2442 + 0.55399 * tw + 0.45535 * ta - 0.0022 * Math.pow(tw, 2.0) + 0.00278 * tw * ta + 3.0;
    },

    /**
     * 겨울철 체감온도 계산 (10월 ~ 익년 4월)
     * @param {number} ta 기온 (℃)
     * @param {number} v 10분 평균 풍속 (m/s) - 풍속이 없는 경우 0.5를 기본값으로 사용
     * @returns {number} 체감온도
     */
    getInWinter(ta, v = 0.5) {
      return 13.12 + 0.6215 * ta - 11.37 * Math.pow(v, 0.16) + 0.3965 * Math.pow(v, 0.16) * ta;
    },

    /**
     * 현재 월수 출력
     * @returns {number} 현재 월 (1-12)
     */
    getCurrentSeason() {
      return new Date().getMonth() + 1; // getMonth()는 0부터 시작하므로 +1
    },

    /**
     * 값의 범위를 제한하는 함수
     * @param {number} value 입력값
     * @param {number} min 최소값
     * @param {number} max 최대값
     * @returns {number} 제한된 값
     */
    clampValue(value, min, max) {
      return Math.max(min, Math.min(max, value));
    },

    /**
     * 체감온도 계산 (실시간)
     * 지금이 몇월인지에 따라 여름 및 겨울 계산공식 적용
     */
    calculateTemperatureFeel() {
      let ta = parseFloat(this.form.temperature) || 0;
      let rh = parseFloat(this.form.humidity) || 0;

      // 온도와 습도 값을 범위 내로 제한
      ta = this.clampValue(ta, -20, 40); // 온도: -20℃ ~ 40℃
      rh = this.clampValue(rh, 0, 100); // 습도: 0% ~ 100%

      // 제한된 값을 다시 form에 적용
      if (this.form.temperature !== ta) {
        this.form.temperature = ta;
      }
      if (this.form.humidity !== rh) {
        this.form.humidity = rh;
      }

      // 유효하지 않은 값인 경우 체감온도를 0으로 설정
      if (ta < -20 || ta > 40 || rh < 0 || rh > 100) {
        this.form.temperature_feel = 0;
        return;
      }

      // 현재 계절에 따라 체감온도 계산 공식 적용
      const season = this.getCurrentSeason();
      let temperatureFeel = 0;

      if (season >= 5 && season <= 9) {
        // 여름철 체감온도 계산 (5월~9월)
        temperatureFeel = this.getInSummer(ta, rh);
      } else {
        // 겨울철 체감온도 계산 (10월~4월)
        // 풍속이 없으므로 기본값 0.5 m/s 사용
        temperatureFeel = this.getInWinter(ta, 0.5);
      }

      this.form.temperature_feel = parseFloat(temperatureFeel.toFixed(1));
    },
  },
};
</script>

<style scoped lang="scss">
table {
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #cdcdcd;
    font-size: 14px;
  }
  th {
    font-weight: 500;
    text-align: center;
    background: #f9f9f9;
    padding: 8px;
  }
  td {
    font-weight: 400;
    padding: 4px;
  }
}
.temp-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  color: #ffffff;
}
</style>
